import React, { useMemo } from "react";
import { motion } from "framer-motion";
import {
  FaCode,
  FaDownload,
  FaBrain,
  FaChartBar,
  FaCogs,
  FaArrowRight,
  FaCheckCircle,
  FaRocket,
} from "react-icons/fa";

/* --------------------------------------------------------------
   Move these keyframes to your global stylesheet (e.g. index.css)
   -------------------------------------------------------------- */
const GLOBAL_STYLES = `
  @keyframes gradient {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }
  .animate-gradient {
    background-size: 200% auto;
    animation: gradient 3s linear infinite;
  }
  @keyframes css-pulse {
    0%, 100% { opacity: 0.2; transform: scale(1); }
    50% { opacity: 0.4; transform: scale(1.05); }
  }
`;

/* --------------------------------------------------------------
   Component
   -------------------------------------------------------------- */
const HowItWorks = () => {
  /* ----- Static data (memoized) ----- */
  const steps = useMemo(
    () => [
      {
        icon: <FaCode className="text-3xl" />,
        number: "01",
        title: "Add It to Your Site",
        desc:
          "We’ll give you a simple code snippet or integration – no tech skills needed & watch leads start flowing instantly from forms, sessions, and UTM magic.",
        color: "from-pink-500 to-rose-500",
      },
      {
        icon: <FaDownload className="text-3xl" />,
        number: "02",
        title: "Capture & Fuse",
        desc: "Every new lead is logged, analyzed, and scored automatically.",
        color: "from-orange-500 to-red-500",
      },
      {
        icon: <FaBrain className="text-3xl" />,
        number: "03",
        title: "Score & Shield",
        desc:
          "Leads hit our secure backend. AI checks for fakes (anomaly alert!), scores them, and links them to your campaigns.",
        color: "from-blue-500 to-cyan-500",
      },
      {
        icon: <FaChartBar className="text-3xl" />,
        number: "04",
        title: "Act & Grow",
        desc:
          "You’ll instantly know what’s working, which leads to follow up, and which to ignore.",
        color: "from-green-500 to-emerald-500",
      },
    ],
    []
  );

  const modules = useMemo(
    () => [
      { icon: <FaCode />, label: "SDK (Trackers/Widgets)", color: "from-pink-500 to-rose-500" },
      { icon: <FaCogs />, label: "Backend API", color: "from-purple-500 to-pink-500" },
      { icon: <FaDownload />, label: "Collectors", color: "from-orange-500 to-red-500" },
      { icon: <FaBrain />, label: "AI/Anomaly", color: "from-blue-500 to-cyan-500" },
      { icon: <FaCogs />, label: "Integrations", color: "from-cyan-500 to-blue-500" },
      { icon: <FaChartBar />, label: "UI (React Pages)", color: "from-green-500 to-emerald-500" },
    ],
    []
  );

  /* ----- Reduced-motion flag (memoized) ----- */
  const prefersReducedMotion = useMemo(
    () => window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    []
  );

  /* ----- Variants – simple, short, and disabled when reduced motion ----- */
  const containerVariants = {
    hidden: prefersReducedMotion ? { opacity: 1 } : { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 15 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
  };

  return (
    <section
      id="how-it-works"
      className="relative overflow-hidden bg-gradient-to-br from-white via-slate-50 to-purple-50 py-24"
    >
      {/* ----- Light background blobs (reduced blur/opacity) ----- */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-0 w-64 h-64 bg-pink-200/20 rounded-full blur-xl [animation:css-pulse 4s ease-in-out infinite] [animation-delay:0s]" />
        <div className="absolute bottom-1/4 right-0 w-64 h-64 bg-cyan-200/20 rounded-full blur-xl [animation:css-pulse 4s ease-in-out infinite] [animation-delay:1s]" />
      </div>

      {/* ----- Subtle grid (low opacity) ----- */}
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,0,0,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.03) 1px, transparent 1px)",
          backgroundSize: "50px 50px",
        }}
      />

      <div className="relative container mx-auto px-6 max-w-6xl">
        {/* ----- Header ----- */}
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
          animate={prefersReducedMotion ? false : { opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.9 }}
            animate={prefersReducedMotion ? false : { opacity: 1, scale: 1 }}
            transition={{ delay: 0.15 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-purple-100 to-pink-100 border border-purple-200 mb-5"
          >
            <FaRocket className="text-purple-600" />
            <span className="text-sm font-semibold text-purple-900">The Smarter Way to Handle Leads</span>
          </motion.div>

          <h2 className="text-4xl md:text-6xl font-black mb-4 leading-tight">
            <span className="block text-slate-900">No confusion. No lost leads.</span>
            <span className="block bg-gradient-to-r from-pink-600 via-purple-600 to-cyan-600 bg-clip-text text-transparent animate-gradient">
              Just clarity and action.
            </span>
          </h2>

          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Don’t sweat the tech – we’ve made it idiot-proof. Here’s the magic in four steps.
          </p>
        </motion.div>

        {/* ----- Timeline (simplified) ----- */}
        <div className="relative mb-20">
          {/* Horizontal line – only on md+ */}
          <motion.div
            initial={prefersReducedMotion ? false : { scaleX: 0 }}
            animate={prefersReducedMotion ? false : { scaleX: 1 }}
            transition={{ duration: 0.9, ease: "easeInOut" }}
            className="hidden md:block absolute left-[10%] right-[10%] top-20 h-0.5 bg-gradient-to-r from-pink-500 via-purple-500 via-cyan-500 to-emerald-500 origin-left rounded-full"
          />

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-4 gap-6"
          >
            {steps.map((step, idx) => (
              <StepCard
                key={idx}
                step={step}
                idx={idx}
                prefersReducedMotion={prefersReducedMotion}
              />
            ))}
          </motion.div>
        </div>

        {/* ----- Modules ----- */}
        <ModulesSection modules={modules} prefersReducedMotion={prefersReducedMotion} />

        {/* ----- Bottom decorative line (optional, very light) ----- */}
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.9 }}
          animate={prefersReducedMotion ? false : { opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="mt-12 flex justify-center"
        >
          <div className="relative">
            <motion.div
              className="h-0.5 w-48 bg-gradient-to-r from-transparent via-purple-500 to-transparent rounded-full"
              animate={prefersReducedMotion ? false : { opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <motion.div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-purple-600"
              animate={prefersReducedMotion ? false : { scale: [1, 1.4, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </div>
        </motion.div>
      </div>

      {/* ----- Global keyframes (move to index.css) ----- */}
      <style>{GLOBAL_STYLES}</style>
    </section>
  );
};

/* --------------------------------------------------------------
   Step Card – extracted to keep the main component lean
   -------------------------------------------------------------- */
const StepCard = React.memo(({ step, idx, prefersReducedMotion }) => {
  const itemVariants = {
    hidden: prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 15 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <motion.div variants={itemVariants} className="relative">
      {/* Number bubble (desktop) */}
      <motion.div
        initial={prefersReducedMotion ? false : { scale: 0 }}
        animate={prefersReducedMotion ? false : { scale: 1 }}
        transition={{ delay: 0.2 + idx * 0.08, type: "spring", stiffness: 250 }}
        className="hidden md:flex absolute left-1/2 -translate-x-1/2 top-16 w-14 h-14 rounded-full bg-white border-4 border-purple-200 shadow-md items-center justify-center z-20"
      >
        <span className={`text-xl font-black bg-gradient-to-r ${step.color} bg-clip-text text-transparent`}>
          {step.number}
        </span>
      </motion.div>

      {/* Card */}
      <motion.div
        whileHover={prefersReducedMotion ? false : { y: -8, scale: 1.02 }}
        transition={{ type: "spring", stiffness: 300 }}
        className="mt-44 md:mt-44 bg-white rounded-2xl p-6 border border-slate-200 hover:border-purple-300 hover:shadow-xl transition-all duration-300"
      >
        <div className={`absolute inset-0 bg-gradient-to-br ${step.color} opacity-0 hover:opacity-5 transition-opacity rounded-2xl`} />

        <div className="relative space-y-3">
          {/* Icon */}
          <motion.div
            className={`w-16 h-16 rounded-xl bg-gradient-to-br ${step.color} flex items-center justify-center text-white shadow-md`}
            whileHover={prefersReducedMotion ? false : { rotate: 360, scale: 1.1 }}
            transition={{ duration: 0.5 }}
          >
            {step.icon}
          </motion.div>

          {/* Mobile badge */}
          <div className="md:hidden inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-purple-100 to-pink-100 border border-purple-200">
            <span className={`text-xs font-semibold bg-gradient-to-r ${step.color} bg-clip-text text-transparent`}>
              Step {step.number}
            </span>
          </div>

          <h3 className="text-xl font-bold text-slate-900">{step.title}</h3>
          <p className="text-slate-600 text-sm">{step.desc}</p>

          {/* Progress bar */}
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-4">
            <motion.div
              className={`h-full bg-gradient-to-r ${step.color} rounded-full`}
              initial={prefersReducedMotion ? false : { width: 0 }}
              animate={prefersReducedMotion ? false : { width: "100%" }}
              transition={{ delay: 0.6 + idx * 0.1, duration: 0.8 }}
            />
          </div>
        </div>
      </motion.div>

      {/* Arrow between steps (desktop) */}
      {idx < 3 && (
        <motion.div
          className="hidden lg:block absolute -right-10 top-1/2 text-purple-400"
          animate={prefersReducedMotion ? false : { x: [0, 6, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <FaArrowRight className="text-2xl" />
        </motion.div>
      )}
    </motion.div>
  );
});

/* --------------------------------------------------------------
   Modules Section – extracted for readability & memoization
   -------------------------------------------------------------- */
const ModulesSection = React.memo(({ modules, prefersReducedMotion }) => {
  const containerVariants = {
    hidden: prefersReducedMotion ? { opacity: 1 } : { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.12 },
    },
  };
  const itemVariants = {
    hidden: prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
  };

  return (
    <motion.div
      initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
      animate={prefersReducedMotion ? false : { opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="relative"
    >
      <div className="text-center mb-10">
        <div className="flex items-center justify-center gap-2 mb-4">
          <div className="w-6 h-0.5 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full" />
          <FaCogs className="text-purple-600 text-xl" />
          <div className="w-6 h-0.5 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-full" />
        </div>
        <h3 className="text-2xl md:text-4xl font-black text-slate-900 mb-2">
          Modular Architecture
        </h3>
        <p className="text-base text-slate-600 max-w-xl mx-auto">
          Built with composable components that work in perfect harmony.
        </p>
      </div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4"
      >
        {modules.map((mod, idx) => (
          <motion.div
            key={idx}
            variants={itemVariants}
            whileHover={prefersReducedMotion ? false : { scale: 1.06, y: -4 }}
            className="group relative"
          >
            <div className="h-full bg-white rounded-xl p-4 border border-slate-200 hover:border-purple-300 hover:shadow-lg transition-all flex flex-col items-center text-center space-y-2 min-h-[120px]">
              <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${mod.color} flex items-center justify-center text-white shadow-md`}>
                {mod.icon}
              </div>
              <p className="text-xs font-semibold text-slate-900">{mod.label}</p>
              <div className={`absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r ${mod.color} opacity-0 group-hover:opacity-100 transition-opacity`} />
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Integration note */}
      <motion.div
        initial={prefersReducedMotion ? false : { opacity: 0, y: 15 }}
        animate={prefersReducedMotion ? false : { opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.5 }}
        className="mt-10 bg-gradient-to-r from-purple-50 via-pink-50 to-cyan-50 rounded-xl p-6 border border-purple-200 shadow-md"
      >
        <div className="flex flex-col md:flex-row items-center justify-center gap-3 text-center md:text-left">
          <motion.div
            animate={prefersReducedMotion ? false : { scale: [1, 1.15, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center shadow-md"
          >
            <FaCheckCircle className="text-white text-xl" />
          </motion.div>
          <div>
            <p className="text-slate-900 font-semibold text-base">
              Seamless Integration Ready
            </p>
            <p className="text-slate-600 text-sm">
              It’s like hiring a digital assistant for your marketing – one that never sleeps.
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
});

export default HowItWorks;